import { useState, useCallback } from "react";
import { SIDEBAR } from "@/lib/uiConstants";

/**
 * useSidebarPersistence Hook
 * 
 * Manages sidebar expanded/collapsed state with localStorage persistence.
 * 
 * Key behaviors:
 * - Reads localStorage synchronously in useState initializer (no flash on load)
 * - First-time users default to expanded
 * - Invalid values ("true", "1", garbage) treated as missing → expanded
 * - Writes wrapped in try/catch for silent failure
 * - localStorage unavailable → default to expanded, app continues
 */
export function useSidebarPersistence() {
  const [isExpanded, setIsExpanded] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(SIDEBAR.storageKey);
      if (stored === "collapsed") return false;
      if (stored === "expanded") return true;
      // First-time user or invalid value → default to expanded
      return true;
    } catch {
      // localStorage unavailable (privacy mode, blocked, quota) → expanded
      return true;
    }
  });

  const toggle = useCallback(() => {
    setIsExpanded((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR.storageKey, next ? "expanded" : "collapsed");
      } catch {
        // Silent fail - app continues working
      }
      return next;
    });
  }, []);

  const setExpanded = useCallback((value: boolean) => {
    setIsExpanded(value);
    try {
      localStorage.setItem(SIDEBAR.storageKey, value ? "expanded" : "collapsed");
    } catch {
      // Silent fail
    }
  }, []);

  return { isExpanded, toggle, setExpanded };
}
